import { TypedDocumentNode, gql } from '@apollo/client';
import { faEllipsis, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import {
  CSSProperties,
  ComponentProps,
  FC,
  PropsWithChildren,
  ReactNode,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { To } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import IconButton from '@sorare/core/src/atoms/buttons/IconButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { LineupLayout as CoreLineupLayout } from '@sorare/core/src/components/lobby/LineupLayout';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { LineupHeader } from 'components/lineup/LineupHeader';
import { LineupFooter } from 'layouts/LineupLayout/LineupFooter';
import { isFixtureOpened } from 'lib/so5';

import { HideTeamDialog } from './HideTeamDialog';
import { LineupRanking } from './LineupRanking';
import { LineupScoreRow } from './LineupScoreRow';
import { LineupStatus } from './LineupStatus';
import { LineupLayout_so5LeaderboardContender } from './__generated__/index.graphql';

export const StyledLinkBox = styled(LinkBox)`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  border-radius: var(--double-unit);
  overflow: hidden;
  background: var(--c-neutral-300);

  > * {
    z-index: 1;
  }

  --base-dialog-gradient: linear-gradient(
    179deg,
    rgba(var(--c-rgb-neutral-200), 0.85) 0%,
    rgba(var(--c-rgb-neutral-300), 1) 30%
  );

  &.limited {
    background: var(--base-dialog-gradient), var(--c-gradient-limited);
  }

  &.rare {
    background: var(--base-dialog-gradient), var(--c-gradient-rare);
  }

  &.common {
    background: var(--base-dialog-gradient), var(--c-gradient-common);
  }

  &.super_rare {
    background: var(--base-dialog-gradient), var(--c-gradient-superRare);
  }

  &.unique {
    background: var(--base-dialog-gradient), var(--c-gradient-unique);
  }

  &.mix {
    background: var(--base-dialog-gradient), var(--c-gradient-mix);
  }
`;

export const Content = styled(Vertical).attrs({ gap: 2 })`
  flex: 1;
  padding: var(--double-unit);
`;
export const Cards = styled.div`
  flex: 1;
  width: 100%;

  &.cancelled {
    filter: grayscale(1);
  }
`;
const StyledButtonBase = styled(ButtonBase)`
  min-width: 150px;
  padding: var(--unit) var(--double-unit);

  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: rgba(var(--c-rgb-neutral-1000), 0.05);
  }
`;

export type Props = {
  children: ReactNode;
  so5LeaderboardContender?: LineupLayout_so5LeaderboardContender;
  state?: Record<string, unknown>;
  rankingOnTop?: boolean;
  withFooter?: boolean;
  hideLineupsCount?: boolean;
  to?: To;
  className?: string;
  style?: CSSProperties;
} & Pick<
  ComponentProps<typeof LineupStatus>,
  'onClickPrizePool' | 'onClickSchedule'
> &
  Pick<ComponentProps<typeof LineupFooter>, 'onLoadPreviousLineup'>;

export const LineupLayout: FC<PropsWithChildren<Props>> = ({
  children,
  className,
  style,
  so5LeaderboardContender,
  state,
  rankingOnTop = false,
  withFooter = true,
  hideLineupsCount,
  to,
  onClickPrizePool,
  onClickSchedule,
  onLoadPreviousLineup,
}: Props) => {
  const [hideTeamDialogOpened, setHideTeamDialogOpened] = useState(false);

  const { so5Lineup, so5Leaderboard, managerTeam } =
    so5LeaderboardContender || {};

  const isUpcoming =
    so5Leaderboard?.so5Fixture && isFixtureOpened(so5Leaderboard?.so5Fixture);

  return (
    <>
      <StyledLinkBox
        style={style}
        className={classNames(className, so5Leaderboard?.rarityType)}
      >
        <CoreLineupLayout
          header={
            <>
              <LinkOverlay
                to={to}
                as={to ? Link : 'span'}
                viewTransition={!!to}
                state={state}
              >
                <LineupHeader
                  so5LeaderboardContender={so5LeaderboardContender}
                  showLineupsCount={!rankingOnTop && !hideLineupsCount}
                />
              </LinkOverlay>
              {!!managerTeam && isUpcoming && managerTeam.hidable && (
                <LinkOther>
                  <Dropdown
                    forceDesktopBehavior
                    align="right"
                    label={props => (
                      <IconButton
                        {...props}
                        icon={faEllipsis}
                        smaller
                        color="opa"
                      />
                    )}
                  >
                    <StyledButtonBase
                      onClick={() => setHideTeamDialogOpened(true)}
                    >
                      <Horizontal>
                        <FontAwesomeIcon icon={faEyeSlash} />
                        <LabelL>
                          <FormattedMessage {...fantasy.hideTeam} />
                        </LabelL>
                      </Horizontal>
                    </StyledButtonBase>
                  </Dropdown>
                </LinkOther>
              )}
            </>
          }
          subHeader={
            rankingOnTop && (
              <LineupScoreRow
                lineupsCount={
                  so5LeaderboardContender?.so5Leaderboard.so5LineupsCount || 0
                }
                rank={
                  so5LeaderboardContender?.so5Lineup?.so5Rankings[0].ranking
                }
                score={so5LeaderboardContender?.so5Lineup?.so5Rankings[0].score}
                locked={false}
              />
            )
          }
          subFooter={
            !rankingOnTop && (
              <LinkOther as="div">
                <LineupStatus
                  so5LeaderboardContender={so5LeaderboardContender}
                  onClickPrizePool={onClickPrizePool}
                  onClickSchedule={onClickSchedule}
                >
                  <LineupRanking
                    so5LeaderboardContender={so5LeaderboardContender}
                  />
                </LineupStatus>
              </LinkOther>
            )
          }
          footer={
            withFooter && (
              <LinkOther as="div">
                <LineupFooter
                  so5LeaderboardContender={so5LeaderboardContender}
                  onLoadPreviousLineup={onLoadPreviousLineup}
                />
              </LinkOther>
            )
          }
        >
          <Cards
            className={classNames({ cancelled: !!so5Lineup?.cancelledAt })}
          >
            {children}
          </Cards>
        </CoreLineupLayout>
      </StyledLinkBox>

      {hideTeamDialogOpened && so5LeaderboardContender?.managerTeam && (
        <HideTeamDialog
          onClose={() => setHideTeamDialogOpened(false)}
          managerTeam={so5LeaderboardContender?.managerTeam}
          so5Leaderboard={so5LeaderboardContender?.so5Leaderboard}
        />
      )}
    </>
  );
};

LineupLayout.fragments = {
  so5LeaderboardContender: gql`
    fragment LineupLayout_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        cancelledAt
        so5Rankings {
          id
          score
          ranking
        }
      }
      so5Leaderboard {
        slug
        seasonality
        rarityType
        so5Fixture {
          slug
          type
          ...isFixtureOpened_so5Fixture
        }
        so5LeaderboardGroup {
          slug
        }
        ...HideTeamDialog_so5Leaderboard
      }
      managerTeam {
        id
        hidable
        divisionChangingSo5Lineup {
          id
          so5Rankings {
            id
            eligibleDivisionChange
          }
        }
        ...HideTeamDialog_managerTeam
      }
      ...LobbyLineupHeader_so5LeaderboardContender
      ...LineupRanking_so5LeaderboardContender
      ...LineupStatus_so5LeaderboardContender
      ...LineupFooter_so5LeaderboardContender
    }
    ${LineupHeader.fragments.so5LeaderboardContender}
    ${LineupRanking.fragments.so5LeaderboardContender}
    ${LineupStatus.fragments.so5LeaderboardContender}
    ${LineupFooter.fragments.so5LeaderboardContender}
    ${HideTeamDialog.fragments.so5Leaderboard}
    ${HideTeamDialog.fragments.managerTeam}
    ${isFixtureOpened.fragments.so5Fixture}
  ` as TypedDocumentNode<LineupLayout_so5LeaderboardContender>,
};
